import React, { useImperativeHandle, forwardRef, useRef, useEffect, useState } from 'react';
import { Modal } from 'bootstrap';
import uuid from 'react-uuid';
import { toast } from "react-toastify";
import { useSelector } from 'react-redux';
import moment from 'moment';
import DatePicker from "react-datepicker";
import TapIcon from '../../../services/TapIcon';
import TapSelect from '../../../components/ui/TapSelect';
import HttpAPICall from '../../../services/HttpAPICall';
import Loader from '../../../components/ui/Loader/Loader';
import TapHelper from '../../../services/TapHelper';
import { SCHEDULE_API_BASE_URL_2 } from '../../../services/TapApiUrls';

const ReminderModal = forwardRef((props, ref) => {
    const initFormData = {
        reminder_name: '',
        reminder_type: [],
        start_date: '',
        hour: 12,
        minutes: 0,
        loading: false,
        submitting: false,
        contextType: '',
        contextData: null
    };

    const hourOptions = TapHelper.range(0, 23).map(v => ({ label: String(v).padStart(2, '0'), value: v }));
    const minuteOptions = TapHelper.range(0, 59).map(v => ({ label: String(v).padStart(2, '0'), value: v }));

    const [modalId] = useState(uuid());
    const [formId] = useState(uuid());
    const [formData, setReminderFormData] = useState({ ...initFormData });
    const [reminderTypeOptions, setReminderTypeOptions] = useState([]);

    const access_token = useSelector(state => state.auth.access_token);
    const reminderModalElem = useRef(null);
    const reminderModal = useRef(null);

    const formDataUrl = `${SCHEDULE_API_BASE_URL_2}/notification/form_data?report_key=deal_reminder&source=web`;
    const submitUrl = `${SCHEDULE_API_BASE_URL_2}/reminder/save`;

    // Function to check if a time is in the past for today
    const isTimeDisabled = (hour, minute) => {
        const now = moment();
        const selected = moment(formData.start_date).hour(hour).minute(minute);
        if (moment(formData.start_date).isSame(now, 'day')) {
            return selected.isBefore(now);
        }
        return false;
    };

    useImperativeHandle(ref, () => ({
        initReminderModal: (contextType, contextData) => {
            // Auto-generate reminder name from context data
            const autoReminderName = contextData?.header || contextData?.name || contextData?.transaction_id || 'Reminder';
            
            setReminderFormData({
                ...initFormData,
                contextType: contextType,
                contextData: contextData,
                start_date: moment().format('YYYY-MM-DD'),
                hour: moment().hour(),
                minutes: moment().minute(),
                reminder_name: autoReminderName, // Auto-filled
                loading: true
            });
            if (reminderModal.current) {
                reminderModal.current.show();
            }
            loadFormDataInit();
        }
    }));

    useEffect(() => {
        reminderModalElem.current = document.getElementById(modalId);
        reminderModal.current = new Modal(reminderModalElem.current, { keyboard: false, backdrop: 'static' });

        return () => {
            if (reminderModal.current) {
                reminderModal.current.dispose();
            }
        };
    }, [modalId]);

    const loadFormDataInit = () => {
        HttpAPICall.withAthorization('GET', formDataUrl, access_token, {}, {}, 
            (response) => {
                const types = response.data?.available_types || response.data?.notification_types || [];
                const formatted = types.map(t => ({
                    value: t.value || t.key || t.id || t,
                    label: String(t.label || t.name || t).toUpperCase()
                }));
                setReminderTypeOptions(formatted.length > 0 ? formatted : [
                    { value: 'SYSTEM', label: 'IN-APP NOTIFICATION' },
                    { value: 'EMAIL', label: 'EMAIL NOTIFICATION' }
                ]);
            },
            () => {
                toast.error("Failed to load configuration parameters", { position: toast.POSITION.TOP_RIGHT });
            }
        ).then(() => {
            setReminderFormData(prev => ({ ...prev, loading: false }));
        });
    };

    const handleReminderSubmit = (e) => {
        e.preventDefault();
        
        // Check if reminder name is provided
        if (!formData.reminder_name) {
            toast.error("Please enter a reminder name", { position: toast.POSITION.TOP_RIGHT });
            return;
        }

        // Check if at least one reminder type is selected
        if (!formData.reminder_type || formData.reminder_type.length === 0) {
            toast.error("Please select at least one notification type", { position: toast.POSITION.TOP_RIGHT });
            return;
        }

        // Check if start date is selected
        if (!formData.start_date) {
            toast.error("Please select a date", { position: toast.POSITION.TOP_RIGHT });
            return;
        }

        // Check if selected date/time is in the past
        const selectedDateTime = moment(formData.start_date)
            .hour(formData.hour)
            .minute(formData.minutes);
        
        if (selectedDateTime.isBefore(moment())) {
            toast.error("Please select a future date and time", { position: toast.POSITION.TOP_RIGHT });
            return;
        }

        setReminderFormData(prev => ({ ...prev, submitting: true }));

        const formattedDateTime = moment(formData.start_date)
            .hour(formData.hour)
            .minute(formData.minutes)
            .format('YYYY-MM-DD HH:mm:ss');

        const payload = {
            entity_key: 'deal_reminder',
            entity_id: formData.contextData?.transaction_id || formData.contextData?.id,
            name: formData.reminder_name,
            types: Array.isArray(formData.reminder_type) ? formData.reminder_type : [formData.reminder_type],
            frequency: 'onetime',
            start_date: formData.start_date,
            hour: formData.hour,
            minutes: formData.minutes,
            reminder_date_time: formattedDateTime,
            module_context: formData.contextType,
            is_active: 'Y'
        };

        HttpAPICall.withAthorization('POST', submitUrl, access_token, {}, payload, 
            (response) => {
                toast.success(response.data?.msg || "Reminder set successfully!", { position: toast.POSITION.TOP_RIGHT });
                // Close modal after successful submission
                if (reminderModal.current) {
                    reminderModal.current.hide();
                    // Reset form data after closing
                    setReminderFormData(prev => ({ ...prev, submitting: false }));
                    // Call the onSuccess callback to refresh deal detail
                    if (props.onReminderSuccess) {
                        props.onReminderSuccess();
                    }
                }
            }, 
            (error) => {
                toast.error(error?.msg || "Failed to finalize scheduling registration", { position: toast.POSITION.TOP_RIGHT });
                setReminderFormData(prev => ({ ...prev, submitting: false }));
            }
        );
    };

    const reminderModalJsx = () => {
        const { contextData } = formData;
        return (
            <div className="modal fade" id={modalId} tabIndex="-1" ref={reminderModalElem}>
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Set Reminder</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" disabled={formData.submitting || formData.loading} aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            {formData.loading ? <Loader /> : (
                                <form id={formId + 'Form'} onSubmit={handleReminderSubmit}>
                                    
                                    {contextData && (
                                        <div className="alert alert-secondary py-2 mb-3">
                                            <div className="row small">
                                                <div className="col-sm-6"><strong>Transaction ID:</strong> {contextData.transaction_id || contextData.id || '-'}</div>
                                                <div className="col-sm-6"><strong>Header:</strong> {contextData.header || contextData.name || '-'}</div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="row align-items-center my-3">
                                        <div className="col-sm-4"><label className="form-label require">Reminder Name</label></div>
                                        <div className="col-sm-8">
                                            <input 
                                                type="text" 
                                                required 
                                                className="form-control"
                                                placeholder="Enter purpose of reminder"
                                                value={formData.reminder_name}
                                                disabled 
                                                onChange={e => setReminderFormData(d => ({ ...d, reminder_name: e.target.value }))}
                                            />
                                        </div>
                                    </div>

                                    <div className="row align-items-center my-3">
                                        <div className="col-sm-4"><label className="form-label require">Notification Type</label></div>
                                        <div className="col-sm-8">
                                            <TapSelect 
                                                required
                                                isClearable={true}
                                                isMulti={true}
                                                placeholder="Select Notification Type"
                                                options={reminderTypeOptions}
                                                value={reminderTypeOptions.filter(o => formData.reminder_type && formData.reminder_type.includes(o.value))}
                                                changeEvent={o => setReminderFormData(d => ({ 
                                                    ...d, 
                                                    reminder_type: o ? (Array.isArray(o) ? o.map(item => item.value) : [o.value]) : []
                                                }))}
                                            />
                                        </div>
                                    </div>

                                    <div className="row align-items-center my-3">
                                        <div className="col-sm-4"><label className="form-label require">Date & Time</label></div>
                                        <div className="col-sm-4 add_calender_section">
                                            <DatePicker
                                                selected={formData.start_date ? moment(formData.start_date).toDate() : false}
                                                onChange={(v) => setReminderFormData(d => ({ ...d, start_date: v ? moment(v).format('YYYY-MM-DD') : null }))}
                                                dateFormat="dd-MMM-yyyy"
                                                className="form-control form-control-sm"
                                                autoComplete="off" 
                                                placeholderText="Select Date" 
                                                minDate={new Date()}
                                                required
                                            />
                                            <TapIcon.imageIcon icon={TapIcon.CalenderIcon} alt="start_date" className="add_calender-icon" style={{ top: "8px" }} />
                                        </div>
                                        <div className="col-sm-2">
                                            <TapSelect
                                                required
                                                options={hourOptions.map(opt => ({
                                                    ...opt,
                                                    isDisabled: isTimeDisabled(opt.value, formData.minutes)
                                                }))}
                                                value={hourOptions.find(o => o.value === formData.hour)}
                                                changeEvent={o => setReminderFormData(d => ({ ...d, hour: o ? o.value : null }))}
                                                placeholder="HH"
                                            />
                                        </div>
                                        <div className="col-sm-2">
                                            <TapSelect
                                                required
                                                options={minuteOptions.map(opt => ({
                                                    ...opt,
                                                    isDisabled: isTimeDisabled(formData.hour, opt.value)
                                                }))}
                                                value={minuteOptions.find(o => o.value === formData.minutes)}
                                                changeEvent={o => setReminderFormData(d => ({ ...d, minutes: o ? o.value : null }))}
                                                placeholder="MM"
                                            />
                                        </div>
                                    </div>
                                </form>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" disabled={formData.submitting || formData.loading} data-bs-dismiss="modal">Close</button>
                            <button form={formId + 'Form'} type="submit" disabled={formData.submitting || formData.loading} className="btn btn-primary">
                                Save {formData.submitting && <TapIcon.FontAwesomeIcon icon={TapIcon.faSyncAlt} className="fa-spin ms-1" />}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return <>{reminderModalJsx()}</>;
});

export default ReminderModal;